import { CADC, USDC } from "@/config/tokens";
import type { UnwindFormState } from "@/hooks/use-unwind-form";
import { formatToken } from "@/lib/format";

import { SlippageInput } from "./slippage-input";
import { TargetLeverageSlider } from "./target-leverage-slider";
import { UnwindAmountInput } from "./unwind-amount-input";
import { UnwindSourceSelect } from "./unwind-source-select";

type Props = {
  readonly form: UnwindFormState;
};

const fmt = (raw: bigint | null, decimals: number, dp = 2): string =>
  raw == null ? "—" : formatToken(raw, decimals, dp);

const SwapPreview = ({ form }: Props) => {
  const isFull = form.fullClose;
  return (
    <section
      className="rounded-[var(--radius-md)] p-4"
      style={{ border: "1px solid var(--line-2)" }}
    >
      <div
        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-3"
        style={{ color: "var(--ink-4)" }}
      >
        Preview · {isFull ? "Full" : "Partial"} flash-loan unwind
      </div>
      <ul className="space-y-2 font-mono text-[12px] tabular">
        <li className="flex justify-between gap-4">
          <span style={{ color: "var(--ink-3)" }}>Flash-borrow</span>
          <span style={{ color: "var(--ink-1)" }}>
            {fmt(form.partialRepayAssetsRaw, USDC.decimals)} {USDC.symbol}
          </span>
        </li>
        <li className="flex justify-between gap-4">
          <span style={{ color: "var(--ink-3)" }}>Sell collateral</span>
          <span style={{ color: "var(--ink-1)" }}>
            {fmt(form.collateralToSwapRaw, CADC.decimals, 4)} {CADC.symbol}
          </span>
        </li>
        <li className="flex justify-between gap-4">
          <span style={{ color: "var(--ink-3)" }}>Return to you</span>
          <span style={{ color: "var(--accent-jade)" }}>
            {fmt(form.collateralToReturnRaw, CADC.decimals, 4)} {CADC.symbol}
          </span>
        </li>
      </ul>
      <p
        className="mt-3 font-sans text-[11px]"
        style={{ color: "var(--ink-4)" }}
      >
        You spend 0 {USDC.symbol}. The contract borrows USDC for one block,
        repays {isFull ? "your debt" : "the portion you specified"}, and
        settles the loan by selling part of your collateral. It sells a small
        safety margin extra, so any leftover USDC (usually under 1%) is sent
        back to your wallet as dust.
      </p>
    </section>
  );
};

export const UnwindForm = ({ form }: Props) => {
  const isWallet = form.source === "wallet";

  const usdcError =
    form.error && form.error.toLowerCase().includes("usdc") ? form.error : null;
  const cadcError =
    form.error &&
    (form.error.includes("collateral") || form.error.includes("flash loan"))
      ? form.error
      : null;

  const usdcHint = isWallet
    ? form.usdcBalanceRaw != null
      ? `Balance · ${formatToken(form.usdcBalanceRaw, USDC.decimals, 2)} ${USDC.symbol}`
      : "Full debt"
    : "Flash-borrowed";

  const cadcHint = !form.hasCollateral
    ? "No collateral"
    : isWallet
      ? "Derived from USDC + leverage"
      : form.maxReturnCadcHuman != null
        ? `Max receive · ${form.maxReturnCadcHuman.toFixed(2)} ${CADC.symbol}`
        : "Max";

  return (
    <div className="space-y-9">
      <UnwindSourceSelect value={form.source} onChange={form.setSource} />

      <UnwindAmountInput
        label={isWallet ? "USDC you'll pay" : "USDC repaid via flash loan"}
        token="USDC"
        value={isWallet ? form.repayText : form.derivedRepayText}
        placeholder="0.00"
        readOnly={!isWallet}
        onChange={isWallet ? form.setRepayText : undefined}
        onMax={isWallet ? form.setFullRepay : undefined}
        hint={usdcHint}
        error={usdcError}
      />

      <UnwindAmountInput
        label={isWallet ? "Collateral you'll receive" : "Withdraw collateral"}
        token="CADC"
        value={isWallet ? form.derivedCollateralText : form.collateralText}
        placeholder="0.0000"
        readOnly={isWallet}
        onChange={isWallet ? undefined : form.setCollateralText}
        onMax={isWallet ? undefined : form.setFullWithdraw}
        hint={cadcHint}
        error={cadcError}
      />

      <TargetLeverageSlider
        value={form.targetLeverage}
        currentLeverage={form.currentLeverage}
        onChange={form.setTargetLeverage}
      />

      {!isWallet && (
        <>
          <SlippageInput
            bps={form.slippageBps}
            onChange={form.setSlippageBps}
          />
          <SwapPreview form={form} />
        </>
      )}
    </div>
  );
};
